import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
  ADVANCED_QUERY,
} from "../components/AdvancedSearchModal";
import * as Sentry from "@sentry/browser";
import { PageToolbar } from "../components/PageToolbar";
import {
  Button,
  Dropdown,
  Menu,
  Table,
  Tag,
  message,
  Row,
  Col,
} from "antd";
import debounce from "lodash.debounce";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import { AxiosResponse } from "axios";
import moment from "moment";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import ExportData from "../components/ExportData";
import {
  hasActiveFilters,
  parseQueryParams,
  buildQueryString,
  clearAllFilters,
  handleFieldRemove,
} from "../components/TableFilters";
import {
  GET_FIXED_INCOME_REQUEST_STATE_ENUM,
  GET_FIXED_INCOME_REQUEST_SORT_FIELDS,
  SEARCH_FIXED_INCOME_REQUESTS,
} from "../gql/fixed-income-requests";
import { ALL_USERS_QUERY } from "../gql/users";
import { GET_PROVIDERS } from "../gql/loans";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { FixedIncomeRequest, RowSelection } from "../helpers/shared-interfaces";
import { formatSnakeCase, generateNameFromCustomer, getStateColor, capitalizeFirstLetter, fetchEnumOptions, request, showTotalPagination } from "../helpers/utilities";
import entitlements from "../config/entitlements";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  fixedIncomeRequests: FixedIncomeRequest[];
  page: number;
  fixedIncomeRequestsCount: number;
  isLoading: boolean;
  selectedIdsToExport: React.Key[];
  selectedStates: string[];
  stateOptions: Array<{ value: string; label: string; color?: string }>;
  selectedAssignedTo: string[];
  assignedToOptions: Array<{ value: string; label: string }>;
  selectedProducts: string[];
  productOptions: Array<{ value: string; label: string }>;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string }>;
  pageSize: number;
  q: string;
  isSearchingUsers: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

export const fixedIncomeRequestsColumns = [
  {
    title: "Request Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render(createdAt: string): JSX.Element {
      return (
        <span>
          {createdAt
            ? moment(createdAt, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY")
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Customer",
    dataIndex: "customer",
    key: "customer",
    align: "left" as const,
    render(customer: any, record: FixedIncomeRequest): JSX.Element {
      return (
        <NavLink to={`/fixed-income-requests/${record.id}`}>
          {generateNameFromCustomer(record.customer)}
        </NavLink>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "state",
    key: "state",
    render(state: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(state)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(capitalizeFirstLetter(state))}
        </Tag>
      );
    },
  },
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
    render(provider: any): JSX.Element {
      return provider ? (
        <NavLink to={`/providers/${provider.id}`}>{provider.name}</NavLink>
      ) : (
        <span>N/A</span>
      );
    },
  },
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    render(product: any): JSX.Element {
      return <span>{product?.name || "N/A"}</span>;
    },
  },
  {
    title: "Source",
    dataIndex: "source",
    key: "source",
    render(text: string): JSX.Element {
      return (
        <span style={{ textTransform: "capitalize" }}>
          {(text && formatSnakeCase(capitalizeFirstLetter(text))) || "N/A"}
        </span>
      );
    },
  },
  {
    title: "Assigned To",
    dataIndex: "assignedTo",
    key: "assignedTo",
    render(assignedTo: any): JSX.Element {
      return <span>{assignedTo?.name || "Unassigned"}</span>;
    },
  },
];

export default class FixedIncomeRequests extends React.Component<
  RouteComponentProps,
  State
> {
  private _isMounted = false;
  private _dropdownContainer: HTMLDivElement | null = null;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      fixedIncomeRequests: [],
      page: 1,
      fixedIncomeRequestsCount: 0,
      selectedIdsToExport: [],
      selectedStates: [],
      stateOptions: [],
      selectedAssignedTo: [],
      assignedToOptions: [],
      selectedProducts: [],
      productOptions: [],
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      pageSize: 25,
      q: "",
      isSearchingUsers: false,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };
  }

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    const savedFields = JSON.parse(localStorage.getItem("fixedIncomeRequest") || "[]");
    this.setState({ selectedFieldsToDisplay: savedFields });

    await Promise.all([
      this.fetchStateEnums(),
      this.fetchUsers(),
      this.fetchSortFieldOptions(),
      this.fetchProductOptions(),
    ]);

    this.mergeWithLocalState(() => {
      this.performSearch();
    });
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  toggleAdvancedSearchModel = (): void => {
    this.setState((prevState) => ({
      showAdvancedSearchModal: !prevState.showAdvancedSearchModal,
    }));
  };

  handleOnAdvancedSearchError = (error: string): void => {
    message.error(error);
    this.setState({ isLoading: false });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ): void => {
    this.setState({
      isLoading: false,
      fixedIncomeRequests: data.results as FixedIncomeRequest[],
      fixedIncomeRequestsCount: data.count,
      selectedFieldsToDisplay: fields,
      removedField: undefined,
    });
  };

  handleOnFieldRemove = (field: IField): void => {
    handleFieldRemove(this, field, "fixedIncomeRequest");
  };

  fetchStateEnums = async (): Promise<void> => {
    const stateOptions = await fetchEnumOptions(
      GET_FIXED_INCOME_REQUEST_STATE_ENUM,
      {
        includeColor: true,
        labelFormatter: (name) =>
          formatSnakeCase(capitalizeFirstLetter(name)),
        errorMsg: "Failed to fetch state enums",
      },
    );

    if (this._isMounted) {
      this.setState({ stateOptions });
    }
  };

  fetchUsers = async (q?: string): Promise<void> => {
    try {
      if (q) {
        this.setState({ isSearchingUsers: true });
      }
      const response: AxiosResponse = await request({
        query: ALL_USERS_QUERY,
        variables: { page: 1, q: q || undefined },
      });

      const results = response.data?.data?.users?.results || [];

      const assignedToOptions = results.map((user: any) => ({
        value: user.id,
        label: user.name,
      }));

      if (this._isMounted) {
        this.setState({ assignedToOptions, isSearchingUsers: false });
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to fetch users:", error);
      if (this._isMounted) {
        this.setState({ isSearchingUsers: false });
      }
    }
  };

  handleAssignedToSearch = debounce((value: string): void => {
    this.fetchUsers(value);
  }, 300);

  fetchSortFieldOptions = async (): Promise<void> => {
    const sortFieldOptions = await fetchEnumOptions(
      GET_FIXED_INCOME_REQUEST_SORT_FIELDS,
      {
        labelFormatter: (name) =>
          formatSnakeCase(capitalizeFirstLetter(name)),
        errorMsg: "Failed to fetch sort field options",
      },
    );

    if (this._isMounted) {
      this.setState({ sortFieldOptions });
    }
  };

  fetchProductOptions = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: GET_PROVIDERS,
        variables: { page: "1", perPage: "25" },
      });

      const providers: any[] = response.data?.data?.getProviders || [];
      const psl = providers.find((p: any) => p.name === "PSL");
      const productOptions = (psl?.activeProducts || []).map((p: any) => ({
        value: p.id,
        label: p.name,
      }));

      if (this._isMounted) {
        this.setState({ productOptions });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  performSearch = async (): Promise<void> => {
    if (!this._isMounted) return;

    const {
      q,
      page,
      pageSize,
      selectedStates,
      selectedAssignedTo,
      selectedProducts,
      sortField,
      sortDirection,
      selectedFieldsToDisplay,
    } = this.state;

    this.setState({ isLoading: true });

    try {
      let results = [];
      let count = 0;

      if (selectedFieldsToDisplay.length > 0) {
        const fields = selectedFieldsToDisplay.map((f: IField) => ({
          field: f.name,
          operator: f.operator,
          values: f.hasBetween ? [f.value, f.betweenValue] : [f.value],
        }));

        const r: AxiosResponse = await request({
          query: ADVANCED_QUERY,
          variables: {
            model: "fixedIncomeRequest",
            fields,
            page,
            numberPerPage: pageSize,
          },
        });

        const advancedData = r.data?.data?.advancedQuery || {};
        results = advancedData.results || [];
        count = advancedData.count || 0;
      } else {
        const r: AxiosResponse = await request({
          query: SEARCH_FIXED_INCOME_REQUESTS,
          variables: {
            q: q || null,
            page,
            numberPerPage: pageSize,
            filters: {
              states: selectedStates.length > 0 ? selectedStates : null,
              assignedToIds:
                selectedAssignedTo.length > 0 ? selectedAssignedTo : null,
              productIds:
                selectedProducts.length > 0 ? selectedProducts : null,
            },
            orderBy: {
              field: sortField,
              direction: sortDirection,
            },
          },
        });

        const searchData = r.data?.data?.fixedIncomeRequests;

        if (!searchData) {
          if (this._isMounted) {
            message.error("Failed To Load Fixed Income Requests");
          }
          return;
        }

        results = searchData.results || [];
        count = searchData.count;
      }

      if (this._isMounted) {
        this.syncQueryStringToUrl();
        this.setState({
          fixedIncomeRequests: results,
          fixedIncomeRequestsCount: count,
        });
      }
    } catch (error) {
      if (this._isMounted) {
        message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      }
      Sentry.captureException(error);
    } finally {
      if (this._isMounted) {
        this.setState({ isLoading: false });
      }
    }
  };

  performDelayedSearch = debounce(async (): Promise<void> => {
    return await this.performSearch();
  }, DEBOUNCE_TIME);

  mergeWithLocalState = (callback?: () => void): void => {
    const parsed = parseQueryParams({
      includeSort: true,
      includeAssignedTo: true,
      includeProducts: true,
    });

    this.setState(
      {
        q: parsed.q || "",
        page: parsed.page || 1,
        selectedStates: parsed.selectedStates || [],
        selectedAssignedTo: parsed.selectedAssignedTo || [],
        selectedProducts: parsed.selectedProducts || [],
        sortField: parsed.sortField || "CREATED_AT",
        sortDirection: parsed.sortDirection || "DESC",
      },
      callback,
    );
  };

  syncQueryStringToUrl = (): void => {
    const queryStringResult = buildQueryString(this.state, {
      includeSort: true,
      includeAssignedTo: true,
      includeProducts: true,
    });
    this.props.history.replace(queryStringResult);
  };

  // ==================== Event Handlers ====================

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value, page: 1 });
    await this.performDelayedSearch();
  };

  handleStateFilter = (values: string[]): void => {
    this.setState({ selectedStates: values, page: 1 }, () => {
      this.performSearch();
    });
  };

  handleAssignedToFilter = (values: string[]): void => {
    this.setState({ selectedAssignedTo: values, page: 1 }, () => {
      this.performSearch();
    });
  };

  handleProductFilter = (values: string[]): void => {
    this.setState({ selectedProducts: values, page: 1 }, () => {
      this.performSearch();
    });
  };

  handleSortChange = (field: string, direction: string): void => {
    this.setState(
      { sortField: field, sortDirection: direction, page: 1 },
      () => {
        this.performSearch();
      },
    );
  };



  nextPage = async (page: number, pageSize?: number): Promise<void> => {
    if (!this._isMounted) return;

    const ps = typeof pageSize === "number" ? pageSize : this.state.pageSize;

    this.setState({ isLoading: true, page: page, pageSize: ps }, () => {
      this.performSearch();
    });
  };

  onSelectChange = (newSelectedIdsToExport: React.Key[]) => {
    this.setState({ selectedIdsToExport: newSelectedIdsToExport });
  };

  handleCreateMenuClick = (e: { key: string }): void => {
    if (e.key === "new") {
      this.props.history.push("/fixed-income-requests/new?mode=new");
    } else if (e.key === "existing") {
      this.props.history.push("/fixed-income-requests/new?mode=existing");
    }
  };

  render(): JSX.Element | null {
    const {
      fixedIncomeRequests,
      isLoading,
      fixedIncomeRequestsCount,
      page,
      selectedIdsToExport,
      selectedStates,
      stateOptions,
      selectedAssignedTo,
      assignedToOptions,
      selectedProducts,
      productOptions,
      sortField,
      sortDirection,
      pageSize,
      q,
      showAdvancedSearchModal,
      selectedFieldsToDisplay,
      removedField,
    } = this.state;

    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };

    const isFiltering = hasActiveFilters(this.state, {
      includeSort: true,
      includeAssignedTo: true,
      includeProducts: true,
    });

    const tableData = fixedIncomeRequests.map((request) => ({
      ...request,
      key: request.id,
    }));

    return (
      <React.Fragment>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={showAdvancedSearchModal}
          model="fixedIncomeRequest"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
          activeFields={selectedFieldsToDisplay}
        />
          <Card>
            <div className="departments-table-container">
              <PageToolbar
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                searchValue={q}
                onSearchChange={this.handleSearchInput}
                searchPlaceholder="Search by petra id, state, provider, product..."
                actions={
                  <ExportData
                    model={"fixedIncomeRequest"}
                    dataArray={fixedIncomeRequests}
                    customIds={selectedIdsToExport}
                  />
                }
                primaryAction={
                  <EntitledSection
                    entitlement={entitlements.FixedIncomeRequests?.create}
                  >
                    <div style={{ position: "relative" }} ref={(el) => { this._dropdownContainer = el; }}>
                      <Dropdown
                        overlay={
                          <Menu onClick={this.handleCreateMenuClick}>
                            <Menu.Item key="new">New Customer</Menu.Item>
                            <Menu.Item key="existing">Existing Customer</Menu.Item>
                          </Menu>
                        }
                        trigger={["click"]}
                        overlayStyle={{ width: "100%" }}
                        getPopupContainer={() => this._dropdownContainer || document.body}
                      >
                        <Button type="primary" style={{ width: "100%" }}>
                          <PlusOutlined /> Create Fixed Income Request <DownOutlined />
                        </Button>
                      </Dropdown>
                    </div>
                  </EntitledSection>
                }
                totalCount={fixedIncomeRequestsCount}
                currentDataCount={fixedIncomeRequests.length}
                page={page}
                pageSize={pageSize}
                selectedCount={selectedIdsToExport.length}
                filters={
                  <PageToolbar.Filters
                    isFiltering={isFiltering}
                    onClear={() => clearAllFilters(this, "fixedIncomeRequest")}
                    configs={[
                      {
                        label: "Status",
                        options: stateOptions,
                        selectedValues: selectedStates,
                        onChange: this.handleStateFilter,
                        width: 180,
                      },
                      {
                        label: "Assigned To",
                        options: assignedToOptions,
                        selectedValues: selectedAssignedTo,
                        onChange: this.handleAssignedToFilter,
                        onSearch: this.handleAssignedToSearch,
                        loading: this.state.isSearchingUsers,
                        width: 220,
                      },
                      {
                        label: "Product",
                        options: productOptions,
                        selectedValues: selectedProducts,
                        onChange: this.handleProductFilter,
                        width: 220,
                      },
                    ]}
                    sort={{
                      field: sortField,
                      direction: sortDirection,
                      options: this.state.sortFieldOptions,
                      onChange: this.handleSortChange,
                    }}
                  />
                }
                modelName="fixed income requests"
              />

              {/* Table */}
              <Table
              loading={isLoading}
                scroll={{ x: 500 }}
                columns={fixedIncomeRequestsColumns}
                dataSource={tableData}
                pagination={{
                  current: page,
                  pageSize,
                  total: fixedIncomeRequestsCount,
                  onChange: this.nextPage,
                  showSizeChanger: true,
                  showTotal: showTotalPagination,
                }}
                rowSelection={rowSelection}
              />
            </div>
          </Card>
      </React.Fragment>
    );
  }
}
